import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaEdit, 
  FaTrash, 
  FaEnvelope, 
  FaPhoneAlt, 
  FaUserCog, 
  FaUserShield,
  FaInbox, 
  FaSortAlphaDown, 
  FaSortAlphaUp, 
  FaSort,
  FaUser
} from 'react-icons/fa';
import { getUsers, deleteUser, searchUsers } from './userService';
import Pagination from '../../components/shared/Pagination';
import Modal from '../../components/shared/Modal';
import DashboardLayout from '../../layouts/DashboardLayout';
import formatPhone from '../../components/shared/formatPhone';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer,
  Card,
  CardHeader,
  LeadName,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  FilterGroup,
  FilterLabel,
  FilterButton,
  FilterSection
} from '../../components/styled/GridComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ field: 'username', direction: 'asc' });
  const [roleFilter, setRoleFilter] = useState('all');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery && searchQuery.length > 2) {
      const delaySearch = setTimeout(() => {
        handleSearch(searchQuery);
      }, 500);
      return () => clearTimeout(delaySearch);
    } else if (searchQuery === '') {
      fetchUsers();
    }
  }, [searchQuery]);

  const handleSearch = async (query) => {
    if (query.length < 3) return;
    
    setLoading(true);
    try {
      const data = await searchUsers(query);
      setUsers(data);
    } catch (error) {
      console.error('Error searching users:', error);
      setError('Failed to search users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteUser(userToDelete.id);
      setUsers(users.filter(user => user.id !== userToDelete.id));
      setFeedbackMessage('User deleted successfully');
      setFeedbackSuccess(true);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error deleting user:', error);
      setFeedbackMessage('Failed to delete user: ' + error.message);
      setFeedbackSuccess(false);
      setShowFeedback(true);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter((user) => {
      const matchesRole = 
        roleFilter === 'all' || 
        user.role === roleFilter;

      return matchesRole;
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.field) {
        case 'username':
          aValue = a.username || '';
          bValue = b.username || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'role':
          aValue = a.role || '';
          bValue = b.role || '';
          break;
        default:
          aValue = a[sortConfig.field] || '';
          bValue = b[sortConfig.field] || '';
      }

      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [users, roleFilter, sortConfig]);

  const currentUsers = filteredAndSortedUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return '#e74c3c'; // Red for admin
      case 'manager':
        return '#f39c12'; // Orange for manager
      case 'user':
        return '#3498db'; // Blue for user
      default:
        return '#7f8c8d'; // Gray for others
    }
  };

  return ( 
    <DashboardLayout>
      <ResponsiveHeader
        title="User Management"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/users/new')}
      />

      {loading && <LoadingSpinner />}

      {!loading && (
        <>
          <FilterSection>
            <FilterGroup>
              <FilterLabel>Sort by:</FilterLabel>
              <FilterButton 
                isActive={sortConfig.field === 'username'}
                onClick={() => handleSort('username')}
              >
                {sortConfig.field === 'username' ? 
                  (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
                  : <FaSort />
                }
                Username
              </FilterButton>
              <FilterButton 
                isActive={sortConfig.field === 'email'}
                onClick={() => handleSort('email')}
              >
                {sortConfig.field === 'email' ? 
                  (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
                  : <FaSort />
                }
                Email
              </FilterButton>
              <FilterButton 
                isActive={sortConfig.field === 'role'}
                onClick={() => handleSort('role')}
              >
                {sortConfig.field === 'role' ? 
                  (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
                  : <FaSort />
                }
                Role
              </FilterButton>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Role:</FilterLabel>
              <FilterButton
                isActive={roleFilter === 'all'}
                onClick={() => setRoleFilter('all')}
              >
                All
              </FilterButton>
              <FilterButton
                isActive={roleFilter === 'admin'}
                onClick={() => setRoleFilter('admin')}
              >
                Admin
              </FilterButton>
              <FilterButton
                isActive={roleFilter === 'manager'}
                onClick={() => setRoleFilter('manager')}
              >
                Manager
              </FilterButton>
              <FilterButton
                isActive={roleFilter === 'user'}
                onClick={() => setRoleFilter('user')}
              >
                User
              </FilterButton>
            </FilterGroup>
          </FilterSection>

          <GridContainer>
            {currentUsers.length === 0 ? (
              <EmptyState>
                <FaInbox size={48} />
                <p>No users found. Try adjusting your search or add a new user.</p>
              </EmptyState>
            ) : (
              currentUsers.map((user) => (
                <Card key={user.id}>
                  <CardHeader>
                    <LeadName>{user.username}</LeadName>
                    <StatusBadge color={getRoleBadgeColor(user.role)}>
                      {user.role === 'admin' ? (
                        <>
                          <FaUserShield style={{ marginRight: '5px' }} />
                          Admin
                        </>
                      ) : user.role === 'manager' ? (
                        <>
                          <FaUserCog style={{ marginRight: '5px' }} />
                          Manager
                        </>
                      ) : (
                        <>
                          <FaUser style={{ marginRight: '5px' }} />
                          User
                        </>
                      )}
                    </StatusBadge>
                  </CardHeader>

                  <CardContent>
                    <InfoRow>
                      <FaEnvelope />
                      <span>{user.email}</span>
                    </InfoRow>
                    
                    {user.phoneNumber && (
                      <InfoRow>
                        <FaPhoneAlt />
                        <span>{formatPhone(user.phoneNumber)}</span>
                      </InfoRow>
                    )}
                  </CardContent>

                  <ActionIconsContainer>
                    <ActionButton 
                      onClick={() => navigate(`/users/edit/${user.id}`)}
                      title="Edit User"
                    >
                      <FaEdit />
                    </ActionButton>

                    <ActionButton 
                      onClick={() => {
                        setUserToDelete(user);
                        setDeleteModalOpen(true);
                      }}
                      title="Delete User"
                    >
                      <FaTrash />
                    </ActionButton>
                  </ActionIconsContainer>
                </Card>
              ))
            )}
          </GridContainer>
        </>
      )}

      {!loading && filteredAndSortedUsers.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredAndSortedUsers.length}
          paginate={(pageNumber) => setCurrentPage(pageNumber)}
          currentPage={currentPage}
        />
      )}

      {deleteModalOpen && (
        <Modal
          title="Delete User"
          message={`Are you sure you want to delete user ${userToDelete?.username || ''}? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          isOpen={deleteModalOpen}
        />
      )}

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={handleFeedbackComplete}
      />
    </DashboardLayout>
  );
};

export default UserList;